import React from "react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { GlassCard, SectionHeader } from "../components/ui/UIComponents";
import { getDistrictInfo, calcWorkProbability, getProximityLabel } from "../data/indiaData";

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD — AI Results Page
// ─────────────────────────────────────────────────────────────────────────────

const DEMO_RESULT = {
  topCareer: "Full Stack Developer",
  successProbability: 82,
  riskLevel: "Low",
  aiSummary: "Based on your profile, Full Stack Development aligns best with your technical skills and the job market demand in your region. Your college placement rate and metro proximity give you a strong head start.",
  fullAnalysis: "Your engineering background combined with programming skills in Python and web technologies places you in the top 30% of candidates for software roles. The proximity to a major metro city significantly boosts your opportunity score. Focus on React.js and Node.js to unlock full-stack roles paying ₹6–12 LPA within 12 months.",
  careerScores: [
    { name: "Full Stack Dev",   score: 82 },
    { name: "Data Science",     score: 70 },
    { name: "Cloud Engineer",   score: 65 },
    { name: "Govt Services",    score: 58 },
    { name: "Business",         score: 40 },
  ],
  salaryData: [
    { year: "Now",  salary: 4.5  },
    { year: "2yr",  salary: 8    },
    { year: "5yr",  salary: 16   },
    { year: "10yr", salary: 30   },
  ],
  skillRadar: [
    { skill: "Technical",     A: 72, B: 90 },
    { skill: "Soft Skills",   A: 65, B: 80 },
    { skill: "Domain",        A: 58, B: 85 },
    { skill: "Communication", A: 70, B: 85 },
    { skill: "Leadership",    A: 50, B: 75 },
  ],
  pieData: [
    { name: "IT / Software",  value: 45 },
    { name: "Govt Jobs",      value: 22 },
    { name: "Startups",       value: 18 },
    { name: "Business",       value: 10 },
    { name: "Others",         value: 5  },
  ],
  strengths:  ["Strong programming background","Good problem-solving aptitude","Relevant degree for IT roles","English + regional language proficiency"],
  skillGaps:  ["React.js / Next.js","Git & Version Control","System Design & DSA","Cloud fundamentals (AWS)"],
  roadmap: [
    { phase: "Month 1–2", title: "Foundation",   steps: ["Complete DSA (Arrays, Trees, DP)","HTML/CSS/JS deep dive","Git & GitHub setup"] },
    { phase: "Month 3–4", title: "Core Build",   steps: ["React.js full course","Node.js + REST APIs","Build 2 portfolio projects"] },
    { phase: "Month 5–6", title: "Job Ready",    steps: ["Resume & LinkedIn optimization","Apply to 20+ companies","Mock interviews daily"] },
    { phase: "Year 1+",   title: "Growth",       steps: ["Target ₹8–10 LPA roles","Learn AWS basics","Explore product companies"] },
  ],
  platforms:    ["NPTEL (Free IIT Courses)","FreeCodeCamp","Google Career Certificates","Coursera (Financial Aid)","YouTube + CS50"],
  scholarships: ["NSP – National Scholarship Portal","State Merit Scholarship","AICTE Pragati / Saksham","PM-Yasasvi Scheme","Infosys / TCS Foundation Grants"],
  trendingNow:  ["Generative AI (LLMs)","Full Stack Dev","Cloud & DevOps","Cybersecurity","Data Analytics"],
  futureTrends: ["AI/ML Engineering","Quantum Computing","Web3 / Blockchain","AR / VR Dev","Robotics Automation"],
  localDemand:  ["Web Developer","IT Support","Mobile App Dev","BPO / KPO","Govt Exam Coaching"],
  globalDemand: ["Cloud Architect","DevOps Engineer","Data Scientist","AI Researcher","Cybersecurity Analyst"],
};

const PIE_COLORS = ["#22d3ee","#3b82f6","#a855f7","#10b981","#f59e0b"];

const ScoreCard = ({ label, score, color, icon }) => (
  <div style={{
    background: "rgba(13,27,46,0.75)",
    backdropFilter: "blur(12px)",
    border: `1px solid ${color}25`,
    borderRadius: 12, padding: "18px 16px", textAlign: "center",
    transition: "transform 0.2s, box-shadow 0.2s",
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 8px 24px ${color}20`; }}
    onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
  >
    <div style={{ fontSize: 26, marginBottom: 8 }}>{icon}</div>
    <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 36, fontWeight: 700, color, lineHeight: 1 }}>
      {score}<span style={{ fontSize: 16 }}>%</span>
    </div>
    <div style={{ fontSize: 11, color: "#6b8aad", fontFamily: "Exo 2, sans-serif", marginTop: 6, letterSpacing: 0.3 }}>{label}</div>
    <div style={{ marginTop: 10, height: 3, background: "rgba(255,255,255,0.04)", borderRadius: 3 }}>
      <div style={{ height: "100%", width: `${score}%`, background: `linear-gradient(90deg,${color},${color}77)`, borderRadius: 3, transition: "width 1s ease" }} />
    </div>
  </div>
);

const TooltipStyle = { background: "#0d1b2e", border: "1px solid rgba(34,211,238,0.25)", borderRadius: 8, fontFamily: "Exo 2, sans-serif", fontSize: 12 };

export default function Dashboard({ result: r, form, onNavigate }) {
  const result = r || DEMO_RESULT;
  const isDemo = !r;

  // Work probability from form data
  const workScore = (form?.state && form?.district)
    ? calcWorkProbability(form.state, form.district, form.college)
    : null;

  const riskColor =
    result.riskLevel === "Low" ? "#10b981" :
    result.riskLevel === "Medium" ? "#f59e0b" : "#ef4444";

  return (
    <div style={{ background: "#060d1a", minHeight: "calc(100vh - 64px)", padding: "28px 20px 60px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Demo notice */}
        {isDemo && (
          <div style={{
            padding: "12px 20px", marginBottom: 20,
            background: "rgba(168,85,247,0.08)",
            border: "1px solid rgba(168,85,247,0.25)",
            borderRadius: 10, textAlign: "center",
          }}>
            <span style={{ fontSize: 13, color: "#a855f7", fontFamily: "Exo 2, sans-serif" }}>
              👁️ You are viewing a <strong>sample report</strong>. Complete the assessment to get your personalized AI prediction.
            </span>
            <button
              onClick={() => onNavigate("form")}
              style={{
                marginLeft: 16, padding: "4px 14px",
                background: "rgba(168,85,247,0.12)", border: "1px solid #a855f755",
                color: "#a855f7", borderRadius: 6,
                fontFamily: "Rajdhani, sans-serif", fontSize: 12,
                fontWeight: 700, cursor: "pointer", letterSpacing: 1,
              }}
            >
              START MINE →
            </button>
          </div>
        )}

        {/* ── Report Header ── */}
        <div style={{
          background: "linear-gradient(135deg,#0d1b2e,#0f2035)",
          border: "1px solid rgba(34,211,238,0.18)",
          borderRadius: 14, padding: "22px 26px",
          marginBottom: 22,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 11, color: "#22d3ee", letterSpacing: 3, marginBottom: 4 }}>
              AI CAREER ANALYSIS REPORT
            </div>
            <h1 style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 26, fontWeight: 700, color: "#e2f0ff" }}>
              {form?.fullName || "Career Seeker"} — {form?.qualification || "Graduate"}
            </h1>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 4 }}>
              {[
                form?.state && form?.district && `📍 ${form.district}, ${form.state}`,
                form?.college && `🏛️ ${form.college}`,
                form?.interestedField && `🎯 ${form.interestedField}`,
                `⚠️ Risk: ${result.riskLevel}`,
              ].filter(Boolean).map(item => (
                <span key={item} style={{ fontSize: 12, color: "#6b8aad", fontFamily: "Exo 2, sans-serif" }}>{item}</span>
              ))}
            </div>
          </div>
          <button
            onClick={() => onNavigate("form")}
            style={{
              padding: "8px 20px", background: "transparent",
              border: "1px solid rgba(107,138,173,0.3)",
              color: "#6b8aad", borderRadius: 7,
              fontFamily: "Rajdhani, sans-serif",
              fontSize: 12, fontWeight: 600, letterSpacing: 1,
              cursor: "pointer", textTransform: "uppercase", transition: "all 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#22d3ee"}
            onMouseLeave={e => e.currentTarget.style.color = "#6b8aad"}
          >
            ← New Assessment
          </button>
        </div>

        {/* ── AI Top Prediction Banner ── */}
        <GlassCard glow style={{ marginBottom: 22, background: "linear-gradient(135deg,rgba(34,211,238,0.06),rgba(59,130,246,0.06))" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <div style={{ fontSize: 52 }}>🤖</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 11, color: "#22d3ee", letterSpacing: 2.5, marginBottom: 4 }}>
                AI TOP PREDICTION
              </div>
              <h2 style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 24, color: "#e2f0ff", fontWeight: 700, marginBottom: 6 }}>
                {result.topCareer}
              </h2>
              <p style={{ color: "#94b8d4", fontSize: 13, fontFamily: "Exo 2, sans-serif", lineHeight: 1.65, maxWidth: 540 }}>
                {result.aiSummary}
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center", minWidth: 110 }}>
              <div>
                <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 48, fontWeight: 700, color: "#22d3ee", lineHeight: 1, textAlign: "center", textShadow: "0 0 20px rgba(34,211,238,0.4)" }}>
                  {result.successProbability}<span style={{ fontSize: 22 }}>%</span>
                </div>
                <div style={{ fontSize: 10, color: "#6b8aad", letterSpacing: 1, textAlign: "center" }}>SUCCESS PROB.</div>
              </div>
              {workScore !== null && (
                <div>
                  <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 32, fontWeight: 700, color: "#10b981", lineHeight: 1, textAlign: "center" }}>
                    {workScore}<span style={{ fontSize: 16 }}>%</span>
                  </div>
                  <div style={{ fontSize: 10, color: "#6b8aad", letterSpacing: 1, textAlign: "center" }}>WORK PROB.</div>
                </div>
              )}
              <div style={{
                padding: "4px 12px",
                background: `${riskColor}15`, border: `1px solid ${riskColor}40`,
                borderRadius: 20, fontFamily: "Rajdhani, sans-serif",
                fontSize: 12, color: riskColor, letterSpacing: 1,
              }}>
                {result.riskLevel} Risk
              </div>
            </div>
          </div>
        </GlassCard>

        {/* ── Career Suitability Scores ── */}
        <SectionHeader title="Career Suitability Scores" icon="📊" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(155px,1fr))", gap: 12, marginBottom: 28 }}>
          {result.careerScores.map((c, i) => (
            <ScoreCard
              key={i} label={c.name} score={c.score}
              color={["#22d3ee","#3b82f6","#a855f7","#10b981","#f59e0b"][i % 5]}
              icon={["💻","🔬","☁️","🏛️","💼"][i % 5]}
            />
          ))}
        </div>

        {/* ── Charts Row 1 ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 18, marginBottom: 18 }}>
          <GlassCard>
            <SectionHeader title="Salary Growth (LPA)" icon="📈" />
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={result.salaryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(34,211,238,0.08)" />
                <XAxis dataKey="year" stroke="#6b8aad" tick={{ fontFamily: "Exo 2", fontSize: 11 }} />
                <YAxis stroke="#6b8aad" tick={{ fontFamily: "Exo 2", fontSize: 11 }} tickFormatter={v => `₹${v}L`} />
                <Tooltip contentStyle={TooltipStyle} formatter={v => [`₹${v} LPA`, "Salary"]} />
                <Line type="monotone" dataKey="salary" stroke="#22d3ee" strokeWidth={2} dot={{ fill: "#22d3ee", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </GlassCard>

          <GlassCard>
            <SectionHeader title="Sector Opportunity" icon="🥧" />
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={result.pieData} cx="50%" cy="50%" innerRadius={48} outerRadius={76} paddingAngle={3} dataKey="value">
                  {result.pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % 5]} />)}
                </Pie>
                <Tooltip contentStyle={TooltipStyle} />
                <Legend wrapperStyle={{ fontFamily: "Exo 2", fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </GlassCard>
        </div>

        {/* ── Charts Row 2 ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 18, marginBottom: 22 }}>
          <GlassCard>
            <SectionHeader title="You vs Industry Standard" icon="🎯" />
            <ResponsiveContainer width="100%" height={210}>
              <RadarChart data={result.skillRadar}>
                <PolarGrid stroke="rgba(34,211,238,0.15)" />
                <PolarAngleAxis dataKey="skill" tick={{ fill: "#6b8aad", fontFamily: "Exo 2", fontSize: 10 }} />
                <Radar name="You"      dataKey="A" stroke="#22d3ee" fill="#22d3ee" fillOpacity={0.15} />
                <Radar name="Industry" dataKey="B" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.08} />
                <Legend wrapperStyle={{ fontFamily: "Exo 2", fontSize: 11 }} />
              </RadarChart>
            </ResponsiveContainer>
          </GlassCard>

          <GlassCard>
            <SectionHeader title="Career Score Comparison" icon="📊" />
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={result.careerScores} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(34,211,238,0.08)" />
                <XAxis type="number" domain={[0,100]} stroke="#6b8aad" tick={{ fontFamily: "Exo 2", fontSize: 10 }} />
                <YAxis type="category" dataKey="name" stroke="#6b8aad" tick={{ fontFamily: "Exo 2", fontSize: 10 }} width={100} />
                <Tooltip contentStyle={TooltipStyle} />
                <Bar dataKey="score" fill="#22d3ee" radius={[0,4,4,0]}
                  background={{ fill: "rgba(34,211,238,0.04)", radius: [0,4,4,0] }}
                />
              </BarChart>
            </ResponsiveContainer>
          </GlassCard>
        </div>

        {/* ── Skill Gap ── */}
        <GlassCard style={{ marginBottom: 20 }}>
          <SectionHeader title="Skill Gap Analysis" icon="⚡" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
            <div>
              <p style={{ fontSize: 11, color: "#10b981", fontFamily: "Rajdhani, sans-serif", letterSpacing: 2, marginBottom: 12 }}>✅ YOUR STRENGTHS</p>
              {result.strengths.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 9, alignItems: "flex-start" }}>
                  <span style={{ color: "#10b981", fontSize: 11, marginTop: 2 }}>●</span>
                  <span style={{ fontSize: 13, color: "#94b8d4", fontFamily: "Exo 2, sans-serif" }}>{s}</span>
                </div>
              ))}
            </div>
            <div>
              <p style={{ fontSize: 11, color: "#ef4444", fontFamily: "Rajdhani, sans-serif", letterSpacing: 2, marginBottom: 12 }}>🔴 SKILLS TO ACQUIRE</p>
              {result.skillGaps.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 9, alignItems: "flex-start" }}>
                  <span style={{ color: "#ef4444", fontSize: 11, marginTop: 2 }}>●</span>
                  <span style={{ fontSize: 13, color: "#94b8d4", fontFamily: "Exo 2, sans-serif" }}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>

        {/* ── Roadmap ── */}
        <GlassCard style={{ marginBottom: 20 }}>
          <SectionHeader title="AI-Generated Roadmap" icon="🗺️" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 14 }}>
            {result.roadmap.map((r, i) => (
              <div key={i} style={{
                background: "rgba(6,13,26,0.6)",
                border: "1px solid rgba(34,211,238,0.09)",
                borderTop: `3px solid ${["#22d3ee","#3b82f6","#a855f7","#10b981"][i]}`,
                borderRadius: 10, padding: 16,
              }}>
                <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 10, color: ["#22d3ee","#3b82f6","#a855f7","#10b981"][i], letterSpacing: 2, marginBottom: 4 }}>{r.phase}</div>
                <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 16, color: "#e2f0ff", marginBottom: 10, fontWeight: 700 }}>{r.title}</div>
                {r.steps.map((s, j) => (
                  <div key={j} style={{ fontSize: 12, color: "#6b8aad", fontFamily: "Exo 2, sans-serif", marginBottom: 5, paddingLeft: 10, borderLeft: "2px solid rgba(34,211,238,0.15)" }}>
                    {s}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </GlassCard>

        {/* ── Financial & Scholarships ── */}
        <GlassCard style={{ marginBottom: 20 }}>
          <SectionHeader title="Scholarships & Free Learning" icon="💰" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <p style={{ fontSize: 11, color: "#f59e0b", fontFamily: "Rajdhani, sans-serif", letterSpacing: 2, marginBottom: 10 }}>🎓 FREE PLATFORMS</p>
              {result.platforms.map((p, i) => (
                <div key={i} style={{ fontSize: 13, color: "#94b8d4", fontFamily: "Exo 2, sans-serif", marginBottom: 7 }}>→ {p}</div>
              ))}
            </div>
            <div>
              <p style={{ fontSize: 11, color: "#a855f7", fontFamily: "Rajdhani, sans-serif", letterSpacing: 2, marginBottom: 10 }}>🏛️ SCHOLARSHIP SCHEMES</p>
              {result.scholarships.map((s, i) => (
                <div key={i} style={{ fontSize: 13, color: "#94b8d4", fontFamily: "Exo 2, sans-serif", marginBottom: 7 }}>→ {s}</div>
              ))}
            </div>
          </div>
        </GlassCard>

        {/* ── Market Trends ── */}
        <GlassCard style={{ marginBottom: 28 }}>
          <SectionHeader title="Market Trend Analysis" icon="🌐" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(185px,1fr))", gap: 14 }}>
            {[
              { label: "🔥 Trending Now",    items: result.trendingNow,  color: "#22d3ee" },
              { label: "🚀 Next 5 Years",    items: result.futureTrends, color: "#a855f7" },
              { label: "📍 Local Demand",    items: result.localDemand,  color: "#10b981" },
              { label: "🌍 Global Demand",   items: result.globalDemand, color: "#3b82f6" },
            ].map(col => (
              <div key={col.label} style={{
                background: "rgba(6,13,26,0.6)",
                border: "1px solid rgba(34,211,238,0.07)",
                borderRadius: 10, padding: 14,
              }}>
                <p style={{ fontSize: 11, color: col.color, fontFamily: "Rajdhani, sans-serif", letterSpacing: 1.5, marginBottom: 10 }}>{col.label}</p>
                {col.items.map((item, j) => (
                  <div key={j} style={{ fontSize: 12, color: "#6b8aad", fontFamily: "Exo 2, sans-serif", marginBottom: 5 }}>• {item}</div>
                ))}
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Full AI Analysis */}
        {result.fullAnalysis && (
          <GlassCard style={{ marginBottom: 28 }}>
            <SectionHeader title="Full AI Analysis" icon="🧠" />
            <p style={{ fontFamily: "Exo 2, sans-serif", fontSize: 14, color: "#94b8d4", lineHeight: 1.85, whiteSpace: "pre-wrap" }}>
              {result.fullAnalysis}
            </p>
          </GlassCard>
        )}

        {/* CTA */}
        <div style={{ textAlign: "center", paddingTop: 8 }}>
          <button
            onClick={() => onNavigate("form")}
            style={{
              padding: "14px 44px",
              background: "linear-gradient(135deg,rgba(34,211,238,0.12),rgba(59,130,246,0.12))",
              border: "1px solid rgba(34,211,238,0.5)",
              color: "#22d3ee", borderRadius: 8,
              fontFamily: "Rajdhani, sans-serif",
              fontSize: 16, fontWeight: 700, letterSpacing: 1.5,
              cursor: "pointer", textTransform: "uppercase", transition: "all 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = "0 0 24px rgba(34,211,238,0.35)"}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
          >
            🔄 Start New Assessment
          </button>
        </div>
      </div>
    </div>
  );
}